import InventoryService from "services/InventoryService";
import { POSaveParams } from "shared/hooks/usePOManagement";

type SaveRows = Parameters<InventoryService["saveIncomingPOs"]>[0];

/**
 * The one way the PO editor writes a PO, shared by the Purchase Orders, Inventory and Cashflow
 * pages: rename it (server-side, in place — the NetSuite link, marks and WRO allocations move
 * with it), drop the lines the person removed, then save the lines on screen. Throws with the
 * reason at the first step that fails, so the page can show it and re-fetch.
 *
 * A rename used to be "delete every line under the old number, save under the new one" from the
 * browser. That lost everything attached to the old lines, and when the editor's remembered
 * "original number" was stale it deleted a different PO (266A CA, 2026-09-21).
 */
export async function persistPO(inventoryService: InventoryService, params: POSaveParams, rows: SaveRows) {
  const renamed = !params.isNewPO && !!params.originalPONumber && params.originalPONumber !== params.poNumber;
  if (renamed) {
    // Once the server has renamed, the editor must know it even if a later step fails: a retry
    // would otherwise ask to rename the old number again and be told it no longer exists
    try {
      await inventoryService.renamePO(params.originalPONumber, params.poNumber);
    } catch (error: any) {
      if (error?.renamed) params.onRenamed?.();
      throw error;
    }
    params.onRenamed?.();
  }

  if (!params.isNewPO) {
    const keptIds = new Set(params.lineItems.map(li => li.inventoryId));
    for (const li of params.originalLineItems.filter(orig => orig.inventoryId && !keptIds.has(orig.inventoryId))) {
      const deleted = await inventoryService.deleteIncomingPO(li.inventoryId, params.poNumber);
      if (!deleted) throw new Error(`Failed to remove ${li.inventoryName || li.inventoryId} from PO ${params.poNumber}`);
    }
  }

  if (rows.length > 0) await inventoryService.saveIncomingPOs(rows, params.isNewPO);
}
